import { Alert, Checkbox, Form, Modal, Select, message, Button } from "antd";
import { useForm } from "antd/lib/form/Form";
import { DATE_FORMAT } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { AktDatePicker } from "components/aktDatePicker";
import { useFetchCommunicationMethodsQuery } from "features/communicationMethodsTable/communicationMethodsAPI";
import { useCommunicationMethodPermissions } from "features/communicationMethodsTable/hooks/useCommunicationMethodPermissions";
import {
  useCreateTaskMutation,
  useFetchDebtorAccountsQuery,
} from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchDebtorPacketedDebtorsQuery } from "features/debtorProfile/debtorProfileAPI";
import { useFetchDebtorProfileQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import moment from "moment-timezone";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { camelToWords, downloadFileFromUrl, formatCurrency, snakeToCamelCase } from "common/utils";
import { useState, useMemo } from "react";
import { useDownloadInHouseLettersMutation } from "features/documentCenter/documentCenterAPI";
import currency from "currency.js";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 400px;
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 12px;
  white-space: pre-wrap;
`;

function SendLetterSequenceModal({ title, open, onOk, onCancel }) {
  const [form] = useForm();
  const { debtorId } = useParams();
  const [createTask, { isLoading: isCreateTaskLoading }] = useCreateTaskMutation();
  const [downloadInHouseLetters, { isLoading: isInHouseLettersDownloading }] =
    useDownloadInHouseLettersMutation();
  const { data: communicationMethods, isLoading: isCommunicationMethodsLoading } =
    useFetchCommunicationMethodsQuery();
  const { filterAllowedCommunicationMethods } = useCommunicationMethodPermissions();
  const { data: accounts, isLoading: isAccountsLoading } = useFetchDebtorAccountsQuery({
    debtorId,
    isIncludePacketedAccounts: true,
  });
  const { data: packetedDebtorsResponse } = useFetchDebtorPacketedDebtorsQuery({ debtorId });
  const { data: currentDebtor } = useFetchDebtorProfileQuery({ debtorId });

  const [previousErrorMsg, setPreviousErrorMsg] = useState(null);
  const [actionText, setActionText] = useState("Schedule");
  const [selectAll, setSelectAll] = useState(false);

  // Build complete list of packeted debtors (API doesn't return current debtor, so we append it)
  const packetedDebtors = useMemo(() => {
    if (!currentDebtor) return [];
    return [...(packetedDebtorsResponse?.results || []), currentDebtor];
  }, [packetedDebtorsResponse, currentDebtor]);

  // Group accounts by debtor for organized dropdown
  const accountGroups = useMemo(() => {
    if (!accounts || !packetedDebtors?.length) return [];

    const packetedDebtorIds = new Set(packetedDebtors.map((d) => d.id));
    const grouped = {};
    accounts
      .filter((account) => !account.statusIsExcludedFromCommunication)
      .forEach((account) => {
        // Find matching debtors in account.debtors that are in packeted list
        const matchingDebtors = account.debtors?.filter((d) => packetedDebtorIds.has(d.id)) || [];

        matchingDebtors.forEach((debtor) => {
          if (!grouped[debtor.id]) {
            grouped[debtor.id] = {
              debtorId: debtor.id,
              debtorName: debtor.name,
              debtorExternalId: debtor.externalId,
              isCurrentDebtor: debtor.id === parseInt(debtorId, 10),
              accounts: [],
            };
          }

          // Avoid duplicate accounts in same group
          if (!grouped[debtor.id].accounts.find((a) => a.id === account.id)) {
            grouped[debtor.id].accounts.push(account);
          }
        });
      });

    // Sort: current debtor first, then others alphabetically
    return Object.values(grouped).sort((a, b) => {
      if (a.isCurrentDebtor) return -1;
      if (b.isCurrentDebtor) return 1;
      return a.debtorName.localeCompare(b.debtorName);
    });
  }, [accounts, packetedDebtors, debtorId]);

  const allAccountValues = useMemo(
    () =>
      accountGroups.flatMap((group) =>
        group.accounts.map((account) => `${group.debtorId}-${account.id}`),
      ),
    [accountGroups],
  );

  const onSelectAllChange = (e) => {
    setSelectAll(e.target.checked);
    if (!e.target.checked) {
      form.setFieldsValue({ accountIds: [] });
    }
  };

  const handleCancel = () => {
    setSelectAll(false);
    form.resetFields();
    onCancel();
  };

  const letterOptions = filterAllowedCommunicationMethods(communicationMethods)?.reduce(
    (acc, method) => {
      if (!["third_party_letter", "in_house_letter"].includes(method.medium) || method.isArchived) {
        return acc;
      }

      const mediumName = camelToWords(snakeToCamelCase(method.medium));
      const existingMedium = acc.find((option) => option.label === mediumName);
      const isMultipleAccountsPerTask = method.useMultipleAccountsPerTask;
      const optionLabel = method.letterCode ? `${method.letterCode} - ${method.name}` : method.name;
      const option = {
        label: isMultipleAccountsPerTask ? `${optionLabel} (Multiple Accounts)` : optionLabel,
        value: method.id,
      };

      if (existingMedium) {
        existingMedium.options.push(option);
        return acc;
      }

      return [
        ...acc,
        {
          label: mediumName,
          options: [option],
        },
      ];
    },
    [],
  );

  const onSchedule = async (values) => {
    // Build selected_debtor_account from selected debtor-account pairs
    const selectedDebtorAccounts = (selectAll ? allAccountValues : values.accountIds).map(
      (value) => {
        const [selectedDebtorId, accountId] = value.split("-");
        return {
          debtorId: parseInt(selectedDebtorId, 10),
          accountId: parseInt(accountId, 10),
        };
      },
    );

    const result = await createTask({
      debtorId,
      selectedDebtorAccounts,
      communicationMethodId: values.communicationMethodId,
      scheduledDate: values.scheduledDate,
    });

    if ("data" in result) {
      await onOk();
      form.resetFields();
      setSelectAll(false);
      return message.success(`Letter(s) scheduled to send on ${values.scheduledDate}`);
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
      const errors = result.error?.data?.error?.details?.errors;
      if (errors) {
        setPreviousErrorMsg(errors.join("\n"));
      }
    }
  };

  const onDownload = async (values) => {
    try {
      // Build selected_debtor_account from selected debtor-account pairs (same as onSchedule)
      const selectedDebtorAccounts = (selectAll ? allAccountValues : values.accountIds).map(
        (value) => {
          const [selectedDebtorId, accountId] = value.split("-");
          return {
            debtorId: parseInt(selectedDebtorId, 10),
            accountId: parseInt(accountId, 10),
          };
        },
      );

      const result = await createTask({
        debtorId,
        selectedDebtorAccounts,
        communicationMethodId: values.communicationMethodId,
        scheduledDate: values.scheduledDate,
      });
      if ("error" in result) {
        throw result.error;
      }

      // Then download the letters
      const letterTemplateIds = communicationMethods?.find(
        (method) => method.id === values.communicationMethodId,
      )?.letterTemplateIds;
      const downloadResult = await downloadInHouseLetters({
        accountIds: selectedDebtorAccounts.map(({ accountId }) => accountId),
        letterTemplateIds,
        startDate: values.scheduledDate,
        endDate: values.scheduledDate,
        renderFormat: values.renderFormat,
        statuses: ["scheduled"],
        useAsyncJob: false,
      });
      if ("data" in downloadResult) {
        downloadFileFromUrl(downloadResult.data.fileUrl, downloadResult.data.filename);
        await onOk();
        await message.success("Starting to download In-House letters");
        form.resetFields();
        setSelectAll(false);
      } else if ("error" in downloadResult) {
        throw downloadResult.error;
      }
    } catch (error) {
      form.setFields(formatError(error));
      const errors = error?.data?.error?.details?.errors;
      if (errors) {
        setPreviousErrorMsg(errors.join("\n"));
      }
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectAll && allAccountValues.length === 0) {
        form.setFields([{ name: "accountIds", errors: ["No eligible accounts available"] }]);
        return;
      }

      // Form post-transformation
      values.templateVariables = (values.templateVariables ?? []).reduce((acc, templateField) => {
        acc[templateField.fieldName] = templateField.fieldValue;
        return acc;
      }, {});
      const action = form.getFieldValue(["action"]);
      if (action === "schedule") {
        await onSchedule(values);
      } else if (action === "download") {
        await onDownload(values);
      }
    } catch (error) {
      form.setFields(formatError(error));
      setPreviousErrorMsg(error.message);
    }
  };

  const disabledDate = (input) => {
    // Can not select days before today.
    return input && input < moment().startOf("day");
  };

  const onFormChange = () => {
    setPreviousErrorMsg(null);
  };

  return (
    <Modal
      confirmLoading={isCreateTaskLoading}
      maskClosable={false}
      title={title}
      open={open}
      onCancel={onCancel}
      footer={[
        <Button
          key="cancel"
          onClick={onCancel}
          disabled={isCreateTaskLoading || isInHouseLettersDownloading}
        >
          Cancel
        </Button>,
        <Button
          disabled={isCreateTaskLoading || isInHouseLettersDownloading}
          key="submit"
          type="primary"
          loading={isCreateTaskLoading || isInHouseLettersDownloading}
          onClick={onSubmit}
        >
          {form.getFieldValue(["action"]) && form.getFieldValue(["action"]) === "download"
            ? "Download"
            : "Schedule"}
        </Button>,
      ]}
    >
      {previousErrorMsg && <StyledAlert description={previousErrorMsg} type="error" />}

      <StyledForm layout="vertical" form={form} onValuesChange={onFormChange}>
        <Form.Item
          label="Letter"
          name="communicationMethodId"
          rules={[{ required: true, message: "Select a letter" }]}
        >
          <Select
            popupMatchSelectWidth={false}
            placeholder="Select one..."
            loading={isCommunicationMethodsLoading}
            disabled={isCreateTaskLoading || isInHouseLettersDownloading}
            options={letterOptions}
            showSearch
            optionFilterProp="label"
            filterOption={(inputValue, option) => {
              const fullOptionText = option.label;
              // @ts-ignore
              return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
            }}
          />
        </Form.Item>
        <Form.Item
          label={
            <>
              Account(s){" "}
              <Checkbox
                checked={selectAll}
                onChange={onSelectAllChange}
                disabled={isCreateTaskLoading || isInHouseLettersDownloading || isAccountsLoading}
                style={{ marginLeft: 8 }}
              >
                Select all
              </Checkbox>
            </>
          }
          name="accountIds"
          rules={[{ required: !selectAll, message: "Select an account" }]} // Only required for debt validation. This will need to be dynamic in the future when more letter types are supported
        >
          <Select
            popupMatchSelectWidth={false}
            allowClear
            maxTagTextLength={20}
            mode="multiple"
            placeholder="Select accounts..."
            loading={isAccountsLoading}
            disabled={isCreateTaskLoading || isInHouseLettersDownloading || selectAll}
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {accountGroups.map((group) => (
              <Select.OptGroup
                key={`debtor-${group.debtorId}`}
                label={`${group.debtorName} (#${group.debtorExternalId})${
                  group.isCurrentDebtor ? " - Current Debtor" : ""
                }`}
              >
                {group.accounts.map((account) => (
                  <Select.Option
                    key={`account-${group.debtorId}-${account.id}`}
                    value={`${group.debtorId}-${account.id}`}
                    label={`Account #${account.externalId} (Balance: ${formatCurrency(
                      currency(account.totalBalance),
                    )}, Client: ${account.originalCreditorName}, Status Code: ${
                      account.statusCode
                    })`}
                  >
                    Account #{account.externalId} (Balance:{" "}
                    {formatCurrency(currency(account.totalBalance))}, Client:{" "}
                    {account.originalCreditorName}, Status Code: {account.statusCode})
                  </Select.Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="scheduledDate"
          label="Date to Send"
          rules={[{ required: true, message: "Select Date" }]}
          initialValue={moment().format(DATE_FORMAT)}
        >
          <AktDatePicker
            disabledDate={disabledDate}
            disabled={isCreateTaskLoading || isInHouseLettersDownloading}
          />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) => prev?.communicationMethodId !== curr?.communicationMethodId}
        >
          {({ getFieldValue }) => {
            const communicationMethodId = getFieldValue(["communicationMethodId"]);
            const communicationMethod = communicationMethods?.find(
              (method) => method.id === communicationMethodId,
            );
            // Figure out if the letter is an in-house letter. If it is, we give them the option to download. Otherwise,
            // we hide the action and file format selection, because by definition, 3rd party means we don't have the
            // templates in-house to interpolate the variables onto (meaning it's impossible for us to download them).
            const isInHouseLetter = communicationMethod?.medium === "in_house_letter";
            if (!isInHouseLetter) {
              // Although the form item 'action' is not shown, we still want to set the button
              // text to "Schedule" if it's not an in-house letter.
              form.setFieldsValue({ action: "schedule" });
              setActionText("Schedule");
              return null;
            }
            return (
              <>
                <Form.Item
                  label="Action"
                  name="action"
                  rules={[{ required: true, message: "Select an action" }]}
                  initialValue="schedule"
                >
                  <Select
                    popupMatchSelectWidth={false}
                    placeholder="Select one..."
                    loading={isCommunicationMethodsLoading}
                    disabled={isCreateTaskLoading || isInHouseLettersDownloading}
                    options={[
                      { label: "Schedule", value: "schedule" },
                      { label: "Download", value: "download" },
                    ]}
                    optionFilterProp="label"
                    filterOption={(inputValue, option) => {
                      const fullOptionText = option.label;
                      return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
                    }}
                    onChange={(label, _) => setActionText(label)}
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.action !== curr?.action}>
                  {({ _ }) => {
                    const action = getFieldValue(["action"]);
                    if (action !== "download") {
                      return null;
                    }
                    return (
                      <Form.Item
                        label="File Format"
                        name="renderFormat"
                        rules={[{ required: true, message: "Select a file format" }]}
                        initialValue="pdf"
                      >
                        <Select
                          popupMatchSelectWidth={false}
                          placeholder="Select one..."
                          loading={isCommunicationMethodsLoading}
                          disabled={isCreateTaskLoading || isInHouseLettersDownloading}
                          options={[
                            {
                              label: "PDF",
                              value: "pdf",
                            },
                            {
                              label: "Docx",
                              value: "docx",
                            },
                          ]}
                          optionFilterProp="label"
                          filterOption={(inputValue, option) => {
                            const fullOptionText = option.label;
                            // @ts-ignore
                            return fullOptionText
                              ?.toLowerCase()
                              .includes(inputValue?.toLowerCase());
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </>
            );
          }}
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}

export default SendLetterSequenceModal;
